import { v4 as uuidv4 } from 'uuid';

import { APP_CONSTANTS } from '@/config/constants';
import { databaseService } from '@/services/databaseService';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { checkConnection } from '@/services/network';
import { supabase } from '@/services/supabase';
import { pullRemoteChangesForRecovery } from '@/services/syncService';
import { getSyncClientBuild } from '@/services/sync/SyncCanaryFeatureService';
import { runControlledAccountedConflictResolutionBatch } from '@/services/sync/PendingOperationTerminalizationRuntime';
import type {
  RecoveryCheckpointEvent,
  RecoveryIdentity,
  RecoveryPhase,
  RecoveryReceiptVerifier,
  RecoveryResetContext,
  RecoveryState,
} from '@/types/recovery';
import { getLogger } from '@/utils/logger';
import {
  createAppRecoveryCaptureDependencies,
} from './AppRecoveryCaptureDependencies';
import { AppSafeResetExecutor } from './AppSafeResetExecutor';
import { Ed25519RecoveryReceiptVerifier } from './Ed25519RecoveryReceiptVerifier';
import { createAppMultipartRecoveryCaptureAdapter } from './MultipartRecoveryCaptureAdapter';
import { RecoveryEscrowService } from './RecoveryEscrowService';
import { RecoveryGateService } from './RecoveryGateService';
import { RecoveryOrchestrator } from './RecoveryOrchestrator';
import { collectRecoveryPendingInventory } from './RecoveryPendingInventory';
import {
  evaluateRecoveryRuntimeGate,
  type RecoveryRuntimeGateSnapshot,
} from './RecoveryRuntimeGate';
import { SafeResetCoordinator } from './SafeResetCoordinator';
import {
  SupabaseRecoveryEscrowTransport,
  SupabaseRecoveryValidationGateway,
} from './SupabaseRecoveryEscrowRuntime';
import {
  buildRecoveryErrorTelemetry,
  extractRecoveryHttpStatus,
  normalizeRecoveryUserError,
  RECOVERY_USER_SAFE_ERROR_MESSAGE,
} from './recoverySafeErrors';

const logger = getLogger('APP-RECOVERY-RUNTIME');

export const verifyCanonicalPullReceipt = async (
  verifier: Pick<RecoveryReceiptVerifier, 'verify'>,
  context: RecoveryResetContext
): Promise<void> => {
  const receipt = context.receipt.receipt;
  const verification = await verifier.verify(receipt, {
    identity: context.identity,
    recoveryId: context.recoveryId,
    manifestSha512: receipt.manifestSha512,
  });
  if (!verification.valid) {
    throw new Error('Receipt de recovery nao pode autorizar o pull canonico.');
  }
};

export type AppRecoveryRuntimeStatus =
  | 'idle'
  | 'consent_required'
  | 'preserving'
  | 'awaiting_validation'
  | 'verifying'
  | 'completed'
  | 'error';

export interface AppRecoveryRuntimeSnapshot {
  status: AppRecoveryRuntimeStatus;
  phase: RecoveryPhase | null;
  recoveryId: string | null;
  progress: number;
  busy: boolean;
  message: string;
  error: string | null;
  canConsent: boolean;
  canResume: boolean;
  canVerify: boolean;
}

export const getRecoveryServiceReleaseId = (
  releasedRecoveryId: string | null,
  snapshot: Pick<AppRecoveryRuntimeSnapshot, 'status' | 'recoveryId'>
): string | null => (
  snapshot.status === 'completed'
  && Boolean(snapshot.recoveryId)
  && snapshot.recoveryId !== releasedRecoveryId
    ? snapshot.recoveryId
    : null
);

interface RecoveryRuntimeOrchestrator {
  start(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    consentedAt: string;
    pendingItems: Array<{ pendingId: string; sourceDigest: string }>;
  }): Promise<RecoveryState>;
  continueAfterTrustedValidation(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<{ status: 'awaiting_validation' | 'completed'; state: RecoveryState }>;
}

export interface AppRecoveryRuntimeDependencies {
  gate: Pick<RecoveryGateService, 'getState'>;
  orchestrator: RecoveryRuntimeOrchestrator;
  resolveAccountedConflicts(identity: RecoveryIdentity): Promise<unknown>;
  collectPending(): Promise<Array<{ pendingId: string; sourceDigest: string }>>;
  reEvaluateGate(identity: RecoveryIdentity): Promise<RecoveryRuntimeGateSnapshot>;
  createRecoveryId(): string;
  now(): string;
  pollingDelaysMs?: readonly number[];
  schedulePolling?(callback: () => void, delayMs: number): ReturnType<typeof setTimeout>;
  cancelPolling?(timer: ReturnType<typeof setTimeout>): void;
}

export interface RecoveryAutoVerificationEnvironment {
  identity: RecoveryIdentity | null;
  isOnline: boolean;
  isActive: boolean;
}

interface RecoveryValidationDatabase {
  listItems<T>(collection: string, query?: unknown): Promise<T[]>;
}

interface LocalSnapshotRow {
  id: string;
  checklist_id: string;
}

interface LocalSnapshotFieldRow {
  id: string;
  snapshot_id: string;
}

interface LocalChecklistValueRow {
  id: string;
  campo_id?: string | null;
  snapshot_field_id?: string | null;
}

const initialSnapshot = (): AppRecoveryRuntimeSnapshot => ({
  status: 'idle',
  phase: null,
  recoveryId: null,
  progress: 0,
  busy: false,
  message: 'Verificando necessidade de preservacao.',
  error: null,
  canConsent: false,
  canResume: false,
  canVerify: false,
});

const phaseProgress: Record<RecoveryPhase, number> = {
  capture_pending: 5,
  capturing: 15,
  captured: 30,
  uploading: 55,
  awaiting_validation: 85,
  validated: 90,
  reset_authorized: 92,
  pending_archived: 94,
  local_cleared: 96,
  canonical_pulled: 97,
  history_restored: 98,
  verifying: 99,
  completed: 100,
};

const checkpointProgress: Partial<Record<RecoveryCheckpointEvent['name'], number>> = {
  capture_started: 10,
  capture_persisted: 30,
  part_verified: 65,
  manifest_verified: 80,
  validation_requested: 85,
  receipt_persisted: 90,
  reset_authorized: 92,
  pending_archived: 94,
  local_cleared: 96,
  canonical_pulled: 97,
  history_restored: 98,
  recovery_verified: 100,
};

const isPreValidationPhase = (phase: RecoveryPhase): boolean => [
  'capture_pending',
  'capturing',
  'captured',
  'uploading',
].includes(phase);

const isValidationPhase = (phase: RecoveryPhase): boolean => !isPreValidationPhase(phase);

export const isTransientRecoveryPollingError = (error: unknown): boolean => {
  const status = extractRecoveryHttpStatus(error);
  if (status === 408 || status === 429 || (status !== null && status >= 500)) {
    return true;
  }
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';
  return /\b(network|fetch|timeout|timed out|connection|offline)\b/i.test(message);
};

const snapshotFromState = (state: RecoveryState): AppRecoveryRuntimeSnapshot => {
  const hasError = Boolean(state.lastError);
  const awaitingValidation = state.phase === 'awaiting_validation';
  return {
    status: hasError
      ? 'error'
      : state.phase === 'completed'
        ? 'completed'
        : awaitingValidation
          ? 'awaiting_validation'
          : isPreValidationPhase(state.phase)
            ? 'preserving'
            : 'verifying',
    phase: state.phase,
    recoveryId: state.recoveryId,
    progress: phaseProgress[state.phase],
    busy: false,
    message: state.phase === 'completed'
      ? 'Dados preservados e recuperacao concluida.'
      : awaitingValidation
        ? 'O pacote foi enviado. Aguarde a validacao segura e verifique novamente.'
        : isPreValidationPhase(state.phase)
          ? 'Preservando os dados deste aparelho.'
          : 'Validando e restaurando o estado seguro.',
    error: state.lastError ? normalizeRecoveryUserError(state.lastError.message) : null,
    canConsent: false,
    canResume: hasError && isPreValidationPhase(state.phase),
    canVerify: isValidationPhase(state.phase),
  };
};

export const validateCanonicalRecoveryState = async (
  identity: RecoveryIdentity,
  database: RecoveryValidationDatabase = databaseService
): Promise<{
  valid: boolean;
  unaccountedPendingCount: number;
  details: Record<string, number>;
}> => {
  const tables = APP_CONSTANTS.DATABASE.TABLES;
  const [pendingOperations, pendingDeletions, checklists, snapshots, snapshotFields, values] =
    await Promise.all([
      database.listItems<Record<string, unknown>>(tables.PENDING_OPERATIONS.NAME),
      database.listItems<Record<string, unknown>>(tables.PENDING_DELETIONS.NAME),
      database.listItems<{ id: string }>(tables.GUINCHO_CHECKLISTS.NAME, {
        empresa_id: identity.companyId,
      }),
      database.listItems<LocalSnapshotRow>(tables.GUINCHO_CHECKLIST_SNAPSHOTS.NAME, {
        empresa_id: identity.companyId,
      }),
      database.listItems<LocalSnapshotFieldRow>(tables.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME, {
        empresa_id: identity.companyId,
      }),
      database.listItems<LocalChecklistValueRow>(tables.GUINCHO_VALORES_CHECKLIST.NAME, {
        empresa_id: identity.companyId,
      }),
    ]);

  const checklistIds = new Set(checklists.map(row => row.id));
  const snapshotIds = new Set(snapshots.map(row => row.id));
  const snapshotFieldIds = new Set(snapshotFields.map(row => row.id));
  const snapshottedChecklistIds = new Set(snapshots.map(row => row.checklist_id));
  const snapshotsWithFields = new Set(snapshotFields.map(row => row.snapshot_id));
  const missingChecklistSnapshots = checklists.filter(
    row => !snapshottedChecklistIds.has(row.id)
  ).length;
  const snapshotsWithoutFields = snapshots.filter(
    row => !snapshotsWithFields.has(row.id)
  ).length;
  const danglingSnapshots = snapshots.filter(row => !checklistIds.has(row.checklist_id)).length;
  const danglingSnapshotFields = snapshotFields.filter(
    row => !snapshotIds.has(row.snapshot_id)
  ).length;
  const danglingHistoricalValues = values.filter(
    row => row.snapshot_field_id && !snapshotFieldIds.has(row.snapshot_field_id)
  ).length;
  const unmaterializedHistoricalValues = values.filter(
    row => !row.campo_id && !row.snapshot_field_id
  ).length;
  const unaccountedPendingCount = pendingOperations.length + pendingDeletions.length;
  const valid =
    unaccountedPendingCount === 0 &&
    missingChecklistSnapshots === 0 &&
    snapshotsWithoutFields === 0 &&
    danglingSnapshots === 0 &&
    danglingSnapshotFields === 0 &&
    danglingHistoricalValues === 0 &&
    unmaterializedHistoricalValues === 0;

  return {
    valid,
    unaccountedPendingCount,
    details: {
      checklistCount: checklists.length,
      snapshotCount: snapshots.length,
      snapshotFieldCount: snapshotFields.length,
      historicalValueCount: values.filter(row => Boolean(row.snapshot_field_id)).length,
      missingChecklistSnapshots,
      snapshotsWithoutFields,
      danglingSnapshots,
      danglingSnapshotFields,
      danglingHistoricalValues,
      unmaterializedHistoricalValues,
    },
  };
};

export class AppRecoveryRuntime {
  private snapshot = initialSnapshot();
  private readonly listeners = new Set<(snapshot: AppRecoveryRuntimeSnapshot) => void>();
  private readonly pollingDelaysMs: readonly number[];
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingAttempt = 0;
  private pollingIdentity: RecoveryIdentity | null = null;
  private pollingOnline = false;
  private pollingActive = false;
  private verificationInFlight = false;

  constructor(private readonly dependencies: AppRecoveryRuntimeDependencies) {
    this.pollingDelaysMs = dependencies.pollingDelaysMs ?? [2000, 4000, 8000, 15000, 30000];
  }

  getSnapshot(): AppRecoveryRuntimeSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (snapshot: AppRecoveryRuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  resetView(): void {
    this.stopAutoVerification();
    this.publish(initialSnapshot());
  }

  configureAutoVerification(environment: RecoveryAutoVerificationEnvironment): void {
    const previousIdentity = this.pollingIdentity;
    const nextIdentity = environment.identity;
    const identityChanged = (
      previousIdentity?.companyId !== nextIdentity?.companyId ||
      previousIdentity?.userId !== nextIdentity?.userId ||
      previousIdentity?.deviceId !== nextIdentity?.deviceId
    );

    if (identityChanged) {
      this.cancelPollingTimer();
      this.pollingAttempt = 0;
    }

    this.pollingIdentity = nextIdentity;
    this.pollingOnline = environment.isOnline;
    this.pollingActive = environment.isActive;

    if (!nextIdentity || !environment.isOnline || !environment.isActive) {
      this.cancelPollingTimer();
      return;
    }

    this.ensureAutoVerificationScheduled();
  }

  stopAutoVerification(): void {
    this.cancelPollingTimer();
    this.pollingAttempt = 0;
    this.pollingIdentity = null;
    this.pollingOnline = false;
    this.pollingActive = false;
  }

  async hydrate(identity: RecoveryIdentity): Promise<AppRecoveryRuntimeSnapshot> {
    try {
      const state = await this.dependencies.gate.getState(identity);
      if (!state) {
        this.publish({
          ...initialSnapshot(),
          status: 'consent_required',
          message: 'A sincronizacao esta pausada. Preserve os dados antes de continuar.',
          canConsent: true,
        });
      } else {
        this.publish(snapshotFromState(state));
      }
    } catch (error) {
      this.fail(error, null);
    }
    return this.snapshot;
  }

  async consentAndStart(identity: RecoveryIdentity): Promise<AppRecoveryRuntimeSnapshot> {
    if (this.snapshot.busy) return this.snapshot;
    try {
      const existing = await this.dependencies.gate.getState(identity);
      if (existing) return this.resume(identity);
      this.publish({
        ...this.snapshot,
        status: 'preserving',
        progress: 5,
        busy: true,
        message: 'Preparando a preservacao local.',
        error: null,
        canConsent: false,
      });
      await this.dependencies.resolveAccountedConflicts(identity);
      const pendingItems = await this.dependencies.collectPending();
      const state = await this.dependencies.orchestrator.start({
        identity,
        recoveryId: this.dependencies.createRecoveryId(),
        consentedAt: this.dependencies.now(),
        pendingItems,
      });
      this.publish(snapshotFromState(state));
    } catch (error) {
      const state = await this.dependencies.gate.getState(identity).catch(() => null);
      this.fail(error, state);
    }
    return this.snapshot;
  }

  async resume(identity: RecoveryIdentity): Promise<AppRecoveryRuntimeSnapshot> {
    if (this.snapshot.busy) return this.snapshot;
    let state: RecoveryState | null = null;
    try {
      state = await this.dependencies.gate.getState(identity);
      if (!state) return this.hydrate(identity);
      if (!isPreValidationPhase(state.phase)) {
        return this.verifyPreservation(identity);
      }
      this.publish({
        ...snapshotFromState(state),
        status: 'preserving',
        busy: true,
        message: 'Retomando a preservacao do ponto confirmado.',
        error: null,
        canResume: false,
      });
      const resumed = await this.dependencies.orchestrator.start({
        identity,
        recoveryId: state.recoveryId,
        consentedAt: state.consentedAt,
        pendingItems: Object.values(state.pendingItems).map(item => ({
          pendingId: item.pendingId,
          sourceDigest: item.sourceDigest,
        })),
      });
      this.publish(snapshotFromState(resumed));
    } catch (error) {
      const latest = await this.dependencies.gate.getState(identity).catch(() => state);
      this.fail(error, latest);
    }
    return this.snapshot;
  }

  async verifyPreservation(identity: RecoveryIdentity): Promise<AppRecoveryRuntimeSnapshot> {
    this.cancelPollingTimer();
    return this.runVerification(identity);
  }

  private async runVerification(
    identity: RecoveryIdentity,
    automatic = false
  ): Promise<AppRecoveryRuntimeSnapshot> {
    if (this.snapshot.busy || this.verificationInFlight) return this.snapshot;
    this.verificationInFlight = true;
    let state: RecoveryState | null = null;
    try {
      state = await this.dependencies.gate.getState(identity);
      if (!state || isPreValidationPhase(state.phase)) {
        throw new Error('O upload seguro ainda nao foi concluido.');
      }
      this.publish({
        ...snapshotFromState(state),
        status: 'verifying',
        busy: true,
        message: 'Verificando o recibo confiavel e a restauracao.',
        error: null,
        canVerify: false,
      });
      const result = await this.dependencies.orchestrator.continueAfterTrustedValidation(
        identity,
        state.recoveryId
      );
      if (result.status === 'awaiting_validation') {
        this.publish(snapshotFromState(result.state));
        return this.snapshot;
      }

      const gate = await this.dependencies.reEvaluateGate(identity);
      if (gate.blocked || gate.status !== 'allowed') {
        throw new Error('Gate permaneceu bloqueado apos a recuperacao concluida.');
      }
      this.publish(snapshotFromState(result.state));
    } catch (error) {
      const latest = await this.dependencies.gate.getState(identity).catch(() => state);
      if (automatic && isTransientRecoveryPollingError(error)) {
        const telemetry = buildRecoveryErrorTelemetry(error);
        logger.warn('[RECOVERY] Validacao automatica sera repetida:', telemetry);
        const base = latest
          ? snapshotFromState(latest)
          : this.snapshot;
        this.publish({
          ...base,
          status: 'awaiting_validation',
          busy: false,
          message: 'Aguardando conexao estavel para concluir a validacao segura.',
          error: null,
          canVerify: true,
        });
      } else {
        this.fail(error, latest);
      }
    } finally {
      this.verificationInFlight = false;
      this.ensureAutoVerificationScheduled();
    }
    return this.snapshot;
  }

  reportCheckpoint(event: RecoveryCheckpointEvent): void {
    const progress = checkpointProgress[event.name];
    if (progress === undefined || this.snapshot.status === 'error') return;
    this.publish({
      ...this.snapshot,
      progress: Math.max(this.snapshot.progress, progress),
      phase: event.phase,
      recoveryId: event.recoveryId,
    });
  }

  private fail(error: unknown, state: RecoveryState | null): void {
    const telemetry = buildRecoveryErrorTelemetry(error);
    const message = RECOVERY_USER_SAFE_ERROR_MESSAGE;
    logger.error('[RECOVERY] Fluxo controlado falhou:', telemetry);
    const sentryError = new Error('Falha controlada no recovery.');
    sentryError.name = 'AppRecoveryRuntimeError';
    captureSentryExceptionWithScope(sentryError, {
      tags: { flow: 'checklist_recovery', stage: state?.phase ?? 'runtime' },
      context: {
        phase: state?.phase ?? null,
        hasPersistedState: Boolean(state),
        errorName: telemetry.name,
        httpStatus: telemetry.httpStatus,
        messageSignature: telemetry.messageSignature,
      },
      fingerprint: ['checklist_recovery', state?.phase ?? 'runtime'],
      dedupeKey: [
        'checklist-recovery',
        state?.phase ?? 'runtime',
        telemetry.name,
        telemetry.httpStatus ?? 'none',
        telemetry.messageSignature,
      ].join(':'),
      dedupeWindowMs: 30000,
    });
    const base = state ? snapshotFromState(state) : initialSnapshot();
    this.publish({
      ...base,
      status: 'error',
      busy: false,
      message: 'A preservacao continua bloqueada ate esta etapa ser concluida.',
      error: message,
      canConsent: !state,
      canResume: Boolean(state && isPreValidationPhase(state.phase)),
      canVerify: Boolean(state && isValidationPhase(state.phase)),
    });
  }

  private publish(snapshot: AppRecoveryRuntimeSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach(listener => listener(snapshot));
    if (snapshot.status !== 'awaiting_validation') {
      this.cancelPollingTimer();
      if (snapshot.status === 'completed' || snapshot.status === 'error') {
        this.pollingAttempt = 0;
      }
      return;
    }
    this.ensureAutoVerificationScheduled();
  }

  private ensureAutoVerificationScheduled(): void {
    if (
      this.pollingTimer ||
      this.verificationInFlight ||
      !this.pollingIdentity ||
      !this.pollingOnline ||
      !this.pollingActive ||
      this.snapshot.status !== 'awaiting_validation'
    ) {
      return;
    }

    if (this.pollingAttempt >= this.pollingDelaysMs.length) {
      const timeoutMessage =
        'A validacao automatica ainda nao terminou. Use Verificar preservacao para tentar novamente.';
      if (this.snapshot.message !== timeoutMessage) {
        this.snapshot = {
          ...this.snapshot,
          message: timeoutMessage,
          canVerify: true,
        };
        this.listeners.forEach(listener => listener(this.snapshot));
      }
      return;
    }

    const delayMs = this.pollingDelaysMs[this.pollingAttempt];
    const schedule = this.dependencies.schedulePolling ?? setTimeout;
    this.pollingTimer = schedule(() => {
      this.pollingTimer = null;
      if (
        !this.pollingIdentity ||
        !this.pollingOnline ||
        !this.pollingActive ||
        this.snapshot.status !== 'awaiting_validation'
      ) {
        return;
      }
      const identity = this.pollingIdentity;
      this.pollingAttempt += 1;
      void this.runVerification(identity, true);
    }, delayMs);
  }

  private cancelPollingTimer(): void {
    if (!this.pollingTimer) return;
    const cancel = this.dependencies.cancelPolling ?? clearTimeout;
    cancel(this.pollingTimer);
    this.pollingTimer = null;
  }
}

export const createAppRecoveryRuntime = (): AppRecoveryRuntime => {
  const gate = new RecoveryGateService();
  const captureDependencies = createAppRecoveryCaptureDependencies();
  const receiptVerifier = new Ed25519RecoveryReceiptVerifier();
  let runtime: AppRecoveryRuntime | null = null;
  const checkpoint = async (event: RecoveryCheckpointEvent) => {
    runtime?.reportCheckpoint(event);
  };
  const escrow = new RecoveryEscrowService({
    gate,
    capture: createAppMultipartRecoveryCaptureAdapter(captureDependencies),
    transport: new SupabaseRecoveryEscrowTransport(captureDependencies.store),
    validationGateway: new SupabaseRecoveryValidationGateway(),
    receiptVerifier,
    checkpoint,
  });
  const resetExecutor = new AppSafeResetExecutor({
    async verifyCanonicalReady(context: RecoveryResetContext) {
      const { data, error } = await supabase.rpc('get_recovery_restore_readiness_v1', {
        p_recovery_id: context.recoveryId,
        p_client_build: getSyncClientBuild(),
      });
      if (error) throw error;
      const readiness = data as Record<string, unknown> | null;
      if (!readiness || readiness.ready !== true) {
        throw new Error('Estado canonico remoto ainda nao esta pronto para limpeza local.');
      }
    },
    async pullCanonical(context: RecoveryResetContext) {
      await verifyCanonicalPullReceipt(receiptVerifier, context);
      const result = await pullRemoteChangesForRecovery({
        identity: context.identity,
        recoveryId: context.recoveryId,
        manifestSha512: context.receipt.receipt.manifestSha512,
        receipt: context.receipt.receipt,
      });
      if (!result.success) {
        throw new Error(result.message ?? 'Pull canonico de recovery falhou.');
      }
      const validation = await validateCanonicalRecoveryState(context.identity);
      if (!validation.valid) {
        throw new Error('Historico canonico local permanece incompleto apos o pull.');
      }
    },
    async restoreHistorical(context: RecoveryResetContext) {
      const validation = await validateCanonicalRecoveryState(context.identity);
      if (!validation.valid) {
        throw new Error('Historico canonico local permanece incompleto apos o pull.');
      }
    },
    verifyRestored: context => validateCanonicalRecoveryState(context.identity),
  });
  const reset = new SafeResetCoordinator({
    gate,
    connectivity: { isOnline: checkConnection },
    executor: resetExecutor,
    receiptVerifier,
    checkpoint,
  });
  runtime = new AppRecoveryRuntime({
    gate,
    orchestrator: new RecoveryOrchestrator({ gate, escrow, reset }),
    resolveAccountedConflicts: identity => runControlledAccountedConflictResolutionBatch({
      companyId: identity.companyId,
      userId: identity.userId,
    }),
    collectPending: () => collectRecoveryPendingInventory(),
    reEvaluateGate: identity => evaluateRecoveryRuntimeGate({
      companyId: identity.companyId,
      userId: identity.userId,
    }),
    createRecoveryId: uuidv4,
    now: () => new Date().toISOString(),
  });
  return runtime;
};

export const appRecoveryRuntime = createAppRecoveryRuntime();
